/**
 * Automations API - Workflow automation rules
 */
import apiClient from './index'

export interface AutomationRule {
  id: string
  name: string
  description?: string
  trigger_type: string
  trigger_config: Record<string, any>
  action_type: string
  action_config: Record<string, any>
  is_active: boolean
  run_count: number
  last_run_at?: string
  created_by_user_id?: string
  created_at: string
  updated_at: string
}

export interface AutomationLog {
  id: string
  rule_id: string
  rule_name: string
  trigger_type: string
  trigger_data: Record<string, any>
  action_type: string
  action_result: Record<string, any>
  status: 'success' | 'failed'
  error_message?: string
  executed_at: string
}

export interface AutomationOption {
  value: string
  label: string
}

export interface AutomationOptions {
  trigger_types: AutomationOption[]
  action_types: AutomationOption[]
}

export interface AutomationRuleResponse {
  data: AutomationRule
}

export interface AutomationRulesResponse {
  data: AutomationRule[]
}

export interface AutomationLogsResponse {
  data: AutomationLog[]
}

export interface AutomationOptionsResponse {
  data: AutomationOptions
}

/**
 * Get all automation rules
 */
export async function getAutomations(activeOnly = false): Promise<AutomationRule[]> {
  const params = activeOnly ? '?active_only=true' : ''
  const response = await apiClient.get<AutomationRulesResponse>(`/automations${params}`)
  return response.data.data
}

/**
 * Get automation options (trigger/action types)
 */
export async function getAutomationOptions(): Promise<AutomationOptions> {
  const response = await apiClient.get<AutomationOptionsResponse>('/automations/options')
  return response.data.data
}

/**
 * Create a new automation rule
 */
export async function createAutomation(data: {
  name: string
  description?: string
  trigger_type: string
  trigger_config?: Record<string, any>
  action_type: string
  action_config?: Record<string, any>
  is_active?: boolean
}): Promise<AutomationRule> {
  const response = await apiClient.post<AutomationRuleResponse>('/automations', data)
  return response.data.data
}

/**
 * Get an automation rule by ID
 */
export async function getAutomation(ruleId: string): Promise<AutomationRule> {
  const response = await apiClient.get<AutomationRuleResponse>(`/automations/${ruleId}`)
  return response.data.data
}

/**
 * Update an automation rule
 */
export async function updateAutomation(
  ruleId: string,
  data: Partial<AutomationRule>
): Promise<AutomationRule> {
  const response = await apiClient.put<AutomationRuleResponse>(`/automations/${ruleId}`, data)
  return response.data.data
}

/**
 * Delete an automation rule
 */
export async function deleteAutomation(ruleId: string): Promise<void> {
  await apiClient.delete(`/automations/${ruleId}`)
}

/**
 * Toggle an automation rule on/off
 */
export async function toggleAutomation(ruleId: string): Promise<AutomationRule> {
  const response = await apiClient.post<AutomationRuleResponse>(`/automations/${ruleId}/toggle`)
  return response.data.data
}

/**
 * Get execution logs for a rule
 */
export async function getAutomationLogs(ruleId: string, limit = 50): Promise<AutomationLog[]> {
  const response = await apiClient.get<AutomationLogsResponse>(
    `/automations/${ruleId}/logs?limit=${limit}`
  )
  return response.data.data
}

/**
 * Get recent automation logs
 */
export async function getRecentAutomationLogs(limit = 50): Promise<AutomationLog[]> {
  const response = await apiClient.get<AutomationLogsResponse>(
    `/automations/logs?limit=${limit}`
  )
  return response.data.data
}
